import * as React from 'react';
import { cn } from '@heroui/react';
import {
  DocRichTextDocument,
} from '@/pages/knowledge-bases/components/DocRichTextEditor';
import {
  KNOWLEDGE_BASE_HEADER_IMAGE_DISPLAY_ASPECT_RATIO,
  metaBackgroundToMiddleColumnStyle,
  parseKnowledgeBaseMetaBackgroundFromMeta,
  resolveKnowledgeBaseHeaderImageFromRecord,
} from '@/pages/knowledge-bases/components/knowledge-base-meta';

/** 与 {@link DocRichTextDocument} 版心对齐的默认设计态宽度（px），用于缩略图 `scale` 换算 */
export const DOC_THUMBNAIL_DEFAULT_DESIGN_WIDTH_PX = 780;

export type DocThumbnailViewportPadding =
  | number
  | { top?: number; left?: number; right?: number };

export type DocThumbnailPreviewProps = {
  /** 预览视口宽度（px） */
  width: number;
  /** 预览视口高度（px） */
  height: number;
  /** 文档标题（展示用，空则显示「未命名」） */
  documentTitle: string;
  /** Lexical 序列化正文 */
  documentContent: unknown | null;
  /** 知识库 `meta`（含 background、headerImage） */
  meta?: unknown;
  /** 列表/记录上的历史头图 URL */
  coverImage?: string | null;
  /**
   * 设计态横向基准宽度（px），用于 `scale = 内容区宽 / designWidthPx * scaleFactor`。
   * 默认 {@link DOC_THUMBNAIL_DEFAULT_DESIGN_WIDTH_PX}。
   */
  designWidthPx?: number;
  /**
   * 在铺满内容区宽度基础上的额外缩放系数，默认 `1`。
   * 小于 `1` 时内容变窄，且因原点为左上角，右侧易出现空白；需对称留白时请保持 `1` 并改用 `viewportPaddingPx`。
   */
  scaleFactor?: number;
  /**
   * 视口内留白（px），**仅上、左、右**三边（底边恒贴视口底）；不参与 `transform: scale`。
   * 传数字表示上、左、右同为该值。
   */
  viewportPaddingPx?: DocThumbnailViewportPadding;
  /**
   * 包在 `transform: scale(...)` 内的容器 class（与正文一起缩放），用于与编辑器 `px-* pt-*` 等对齐。
   * 默认仅 `box-border` + flex 列，避免隐式 magic padding。
   */
  scaledContentClassName?: string;
  editorKey?: string;
  richTextMinHeightClass?: string;
} & Omit<React.ComponentPropsWithoutRef<'div'>, 'children' | 'style' | 'title' | 'content'>;

function toPadTopLeftRight(p: DocThumbnailViewportPadding | undefined): {
  top: number;
  right: number;
  bottom: number;
  left: number;
} {
  if (p == null) return { top: 0, right: 0, bottom: 0, left: 0 };
  if (typeof p === 'number') return { top: p, right: p, bottom: 0, left: p };
  return {
    top: p.top ?? 0,
    right: p.right ?? 0,
    bottom: 0,
    left: p.left ?? 0,
  };
}

/**
 * 知识库文档缩略预览：固定视口尺寸与可选视口 padding（仅上、左、右）；内部按 `designWidthPx` 与 `scaleFactor` 缩放 {@link DocRichTextDocument}。
 * 背景来自 `meta.background`（无或 `none` 时由 {@link metaBackgroundToMiddleColumnStyle} 回落主题背景）；头图来自 `meta.headerImage` 或 `coverImage`。
 */
export function DocThumbnailPreview({
  width,
  height,
  documentTitle,
  documentContent,
  meta,
  coverImage = null,
  designWidthPx = DOC_THUMBNAIL_DEFAULT_DESIGN_WIDTH_PX,
  scaleFactor = 1,
  viewportPaddingPx,
  scaledContentClassName = 'box-border flex min-h-0 min-w-0 flex-col',
  editorKey,
  richTextMinHeightClass = 'min-h-[160px]',
  className,
  ...rest
}: DocThumbnailPreviewProps) {
  const clipRef = React.useRef<HTMLDivElement>(null);
  const [scale, setScale] = React.useState(0);

  const documentBackground = React.useMemo(() => parseKnowledgeBaseMetaBackgroundFromMeta(meta), [meta]);
  const headerImage = React.useMemo(
    () =>
      resolveKnowledgeBaseHeaderImageFromRecord(
        { meta, coverImage },
        KNOWLEDGE_BASE_HEADER_IMAGE_DISPLAY_ASPECT_RATIO,
      ),
    [meta, coverImage],
  );
  const rootBgStyle = React.useMemo(
    () => metaBackgroundToMiddleColumnStyle(documentBackground),
    [documentBackground],
  );

  const pad = React.useMemo(() => toPadTopLeftRight(viewportPaddingPx), [viewportPaddingPx]);

  React.useEffect(() => {
    const el = clipRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      if (w > 0) setScale((w / designWidthPx) * scaleFactor);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [designWidthPx, scaleFactor, pad.top, pad.right, pad.left]);

  const displayTitle = documentTitle.trim() ? documentTitle : '未命名';

  return (
    <div
      {...rest}
      className={cn('relative shrink-0 overflow-hidden text-foreground', className)}
      style={{ width, height, ...rootBgStyle }}
    >
      <div
        ref={clipRef}
        className="absolute box-border overflow-hidden"
        style={{
          top: pad.top,
          right: pad.right,
          bottom: pad.bottom,
          left: pad.left,
        }}
      >
        {scale > 0 ? (
          <div
            className="pointer-events-none origin-top-left"
            style={{
              width: designWidthPx,
              transform: `scale(${scale})`,
            }}
          >
            <div className={scaledContentClassName}>
              <DocRichTextDocument
                editorKey={editorKey}
                applyDocumentBackground={false}
                documentBackground={documentBackground}
                headerImage={headerImage}
                title={displayTitle}
                content={documentContent}
                scrollable={false}
                richTextMinHeightClass={richTextMinHeightClass}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
